import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const INITIAL_FORM = {
  party_name: "",
  mobile_number: "",
  email: "",
  party_type: "Customer",
  party_category: "",
  opening_balance: 0,
  balance_type: "none",
  credit_period: "",
  credit_limit: "",
  gstin: "",
  pan_number: "",
  billing_address: "",
  shipping_address: ""
};

const CreatePartyModal = ({ open, onClose, editingParty }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingParty) {
      setFormData({
        party_name: editingParty.party_name || "",
        mobile_number: editingParty.mobile_number || "",
        email: editingParty.email || "",
        party_type: editingParty.party_type || "Customer",
        party_category: editingParty.party_category || "",
        opening_balance: editingParty.opening_balance || 0,
        balance_type: editingParty.balance_type || "none",
        credit_period: editingParty.credit_period || "",
        credit_limit: editingParty.credit_limit || "",
        gstin: editingParty.gstin || "",
        pan_number: editingParty.pan_number || "",
        billing_address: editingParty.billing_address || "",
        shipping_address: editingParty.shipping_address || ""
      });
    } else {
      setFormData(INITIAL_FORM);
    }
  }, [editingParty, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.party_name.trim()) {
      toast.error("Party name is required");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData, opening_balance: Number(formData.opening_balance) || 0 };
      if (editingParty) {
        await axios.put(`${API}/parties/${editingParty.id}`, payload);
        toast.success("Party updated successfully");
      } else {
        await axios.post(`${API}/parties`, payload);
        toast.success("Party created successfully");
      }
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-party-modal">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: 'Space Grotesk' }}>
            {editingParty ? "Edit Party" : "Create Party"}
          </DialogTitle>
          <DialogDescription>
            {editingParty ? "Update party details" : "Fill in details to create a new party"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Party Name *</Label>
              <Input data-testid="party-name-input" value={formData.party_name} onChange={(e) => set("party_name", e.target.value)} required className="h-10" placeholder="Enter party name" />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile Number</Label>
              <Input data-testid="party-mobile-input" value={formData.mobile_number} onChange={(e) => set("mobile_number", e.target.value)} className="h-10" placeholder="Phone number" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="party-email-input" type="email" value={formData.email} onChange={(e) => set("email", e.target.value)} className="h-10" placeholder="email@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Party Type</Label>
              <Select value={formData.party_type} onValueChange={(v) => set("party_type", v)}>
                <SelectTrigger data-testid="party-type-select" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Customer">Customer</SelectItem>
                  <SelectItem value="Vendor">Vendor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Party Category</Label>
              <Input data-testid="party-category-input" value={formData.party_category} onChange={(e) => set("party_category", e.target.value)} className="h-10" placeholder="e.g. Retail, Wholesale" />
            </div>
            <div className="space-y-1.5">
              <Label>Opening Balance</Label>
              <Input data-testid="party-balance-input" type="number" value={formData.opening_balance} onChange={(e) => set("opening_balance", e.target.value)} className="h-10" placeholder="0" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Balance Type</Label>
              <Select value={formData.balance_type} onValueChange={(v) => set("balance_type", v)}>
                <SelectTrigger data-testid="balance-type-select" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="to_collect">To Collect</SelectItem>
                  <SelectItem value="to_pay">To Pay</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Credit Period</Label>
              <Input data-testid="credit-period-input" value={formData.credit_period} onChange={(e) => set("credit_period", e.target.value)} className="h-10" placeholder="e.g. 30 Days" />
            </div>
            <div className="space-y-1.5">
              <Label>Credit Limit</Label>
              <Input data-testid="credit-limit-input" value={formData.credit_limit} onChange={(e) => set("credit_limit", e.target.value)} className="h-10" placeholder="e.g. 50000" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>GSTIN</Label>
              <Input data-testid="gstin-input" value={formData.gstin} onChange={(e) => set("gstin", e.target.value)} className="h-10" placeholder="GSTIN number" />
            </div>
            <div className="space-y-1.5">
              <Label>PAN Number</Label>
              <Input data-testid="pan-input" value={formData.pan_number} onChange={(e) => set("pan_number", e.target.value)} className="h-10" placeholder="PAN number" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Billing Address</Label>
              <Textarea data-testid="billing-address-input" value={formData.billing_address} onChange={(e) => set("billing_address", e.target.value)} rows={3} placeholder="Billing address" />
            </div>
            <div className="space-y-1.5">
              <Label>Shipping Address</Label>
              <Textarea data-testid="shipping-address-input" value={formData.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} rows={3} placeholder="Shipping address" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-3">
            <Button data-testid="cancel-party-btn" type="button" variant="outline" onClick={() => onClose(false)} disabled={loading}>Cancel</Button>
            <Button data-testid="submit-party-btn" type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {loading ? "Saving..." : editingParty ? "Update Party" : "Create Party"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePartyModal;
